import React, { useMemo } from "react";
import type { PrintTemplate } from "@repo/types";
import { InfoField } from "../../arbre/print/components/InfoField";
import { SectionHeader } from "../../arbre/print/components/SectionHeader";
import type {
  ResolvedItem,
  ResolvedSection,
} from "../../arbre/print/template/types";
import type { EpaysageCtx } from "./registry";
import { resolveEpaysagePages } from "./variants";
import type { EpaysageBlockCtx } from "./web-blocks";

interface HeaderData {
  organizationName: string;
  organizationAddress?: string;
  organizationCity?: string;
  organizationSiret?: string;
  organizationNumCertification?: string;
  organizationLogo?: string | null;
  user?: { prenom?: string; nom?: string } | null;
}

export interface HtmlEpaysageTemplateProps {
  template: PrintTemplate;
  data: EpaysageCtx["data"];
  inventaireId: number;
  /** Marges de la page : par défaut compactes (aperçu) ; la vraie page d'impression passe celles de la fiche. */
  pageClassName?: string;
  /** Rendu d'un bloc riche. Par défaut : un cadre pointillé. */
  renderBlock?: (item: ResolvedItem, ctx: EpaysageBlockCtx) => React.ReactNode;
}

/** Sans cette option, les navigateurs retirent les fonds colorés à l'impression. */
const KEEP_COLORS_WHEN_PRINTED: React.CSSProperties = {
  printColorAdjust: "exact",
  WebkitPrintColorAdjust: "exact",
};

const colorStyle = (
  background?: string,
  color?: string,
): React.CSSProperties => ({
  ...(background ? { backgroundColor: background } : {}),
  ...(color ? { color } : {}),
});

const placeholder = (item: ResolvedItem) => (
  <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
    {item.label}
  </div>
);

function Header({
  data,
  header,
}: {
  data: HeaderData;
  header: PrintTemplate["header"];
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:gap-6">
      {header.showLogo &&
        (data.organizationLogo ? (
          <img
            src={data.organizationLogo}
            alt={data.organizationName}
            className="h-auto max-h-[200px] w-[200px] shrink-0"
          />
        ) : (
          <div className="flex h-[110px] w-[200px] shrink-0 items-center justify-center border border-border bg-muted">
            <span className="text-xs text-muted-foreground">Logo</span>
          </div>
        ))}
      <div className="min-w-0 text-xs">
        {header.showOrganisation && (
          <>
            <p className="text-sm font-bold">{data.organizationName}</p>
            {data.organizationAddress && <p>{data.organizationAddress}</p>}
            {data.organizationCity && <p>{data.organizationCity}</p>}
          </>
        )}
        {header.showSiret && data.organizationSiret && (
          <p>
            <span className="font-bold">SIRET :</span> {data.organizationSiret}
          </p>
        )}
        {header.showRecenseur && (
          <p className="mt-2">
            <span className="font-bold">Recenseur :</span>{" "}
            {data.user?.prenom?.toUpperCase()} {data.user?.nom?.toUpperCase()}
          </p>
        )}
        {header.showOrganisation && data.organizationNumCertification && (
          <p className="mt-2">
            Certification Dendromap : {data.organizationNumCertification}
          </p>
        )}
      </div>
    </div>
  );
}

function Sections({
  sections,
  ctx,
  renderBlock,
}: {
  sections: ResolvedSection[];
  ctx: EpaysageBlockCtx;
  renderBlock: (item: ResolvedItem, ctx: EpaysageBlockCtx) => React.ReactNode;
}) {
  return (
    <>
      {sections.map((section) => (
        <section key={section.id} className="break-inside-avoid">
          {section.title &&
            (section.plainTitle ? (
              <div className="text-base font-bold text-gray-900 sm:text-lg">
                {section.title}
              </div>
            ) : (
              <SectionHeader
                className="bg-gray-500"
                style={{
                  ...colorStyle(
                    section.style?.titleBg,
                    section.style?.titleColor,
                  ),
                  ...KEEP_COLORS_WHEN_PRINTED,
                }}
              >
                {section.title}
              </SectionHeader>
            ))}
          <div
            className="grid gap-4 py-4 text-xs"
            style={{
              ...(section.style?.background
                ? {
                    backgroundColor: section.style.background,
                    ...KEEP_COLORS_WHEN_PRINTED,
                  }
                : {}),
              gridTemplateColumns: `repeat(${section.columns.length}, minmax(0, 1fr))`,
            }}
          >
            {section.columns.map((column, index) => (
              <div
                key={index}
                className={
                  section.id === "identification" ||
                  column.length === 0 ||
                  column.every((item) => item.kind === "block")
                    ? "min-w-0"
                    : "min-w-0 border-l border-l-2 border-gray-400 pl-2"
                }
              >
                {column.map((item) =>
                  item.kind === "block" ? (
                    <div key={item.key}>{renderBlock(item, ctx)}</div>
                  ) : (
                    <InfoField
                      key={item.key}
                      label={item.label}
                      value={item.value}
                    />
                  ),
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/**
 * Peintre HTML de la fiche de l'espace boisé : ne fait que du style à partir de `resolveEpaysagePages`
 * (aucune décision de mise en page ici).
 */
export function HtmlEpaysageTemplate({
  template,
  data,
  inventaireId,
  renderBlock = placeholder,
  pageClassName = "max-w-5xl px-3 py-4 sm:p-4",
}: HtmlEpaysageTemplateProps) {
  const { header } = template;
  const pages = useMemo(
    () => resolveEpaysagePages(template, data, "web"),
    [template, data],
  );

  return (
    <div className={`page mx-auto bg-card ${pageClassName}`}>
      <Header data={data} header={header} />
      <SectionHeader
        variant="epaysage"
        style={{
          ...colorStyle(header.titleBg, header.titleColor),
          ...KEEP_COLORS_WHEN_PRINTED,
        }}
      >
        FICHE DE SYNTHESE ESPACE BOISE - {inventaireId}
      </SectionHeader>
      <Sections
        sections={pages.fiche}
        ctx={{ essence: null, index: 0 }}
        renderBlock={renderBlock}
      />
      {pages.essences.map((sections, index) => (
        <div key={index} className="min-w-0">
          <Sections
            sections={sections}
            ctx={{ essence: data.essence?.[index], index }}
            renderBlock={renderBlock}
          />
        </div>
      ))}
    </div>
  );
}
